import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// Vegifyit brand green
const VEGIFYIT_GREEN = "#2e7d32";

/**
 * SVG Leaf Icon Component
 * Simple, recognizable leaf shape
 */
const LeafSvg = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22L6.66 19.7C7.14 19.87 7.64 20 8 20C19 20 22 3 22 3C21 5 14 5.25 9 6.25C4 7.25 2 11.5 2 13.5C2 15.5 3.75 17.25 3.75 17.25C7 8 17 8 17 8Z" />
  </svg>
);

const LeafIcon = styled(LeafSvg)`
  width: 1.1em;
  height: 1.1em;
  margin-right: 2px;

  /* Gray tier - monochrome */
  &.tier-gray {
    color: #9ca3af;
  }

  /* Green outline tier */
  &.tier-green-outline {
    color: transparent;
    stroke: ${VEGIFYIT_GREEN};
    stroke-width: 1.5;
  }

  /* Solid green tier */
  &.tier-green-solid {
    color: ${VEGIFYIT_GREEN};
  }
`;

const LeafContainer = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1rem;
  pointer-events: auto;

  &.has-tooltip {
    cursor: help;
  }
`;

const Tooltip = styled.span`
  visibility: hidden;
  opacity: 0;
  position: fixed;
  z-index: 99999;
  background-color: #1f2937;
  color: #fff;
  text-align: center;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  pointer-events: none;
  transition: opacity 0.15s ease-in-out;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: calc(100vw - 20px);

  @media (max-width: 400px) {
    white-space: normal;
    max-width: 200px;
  }
`;

const TooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  pointer-events: auto;

  &:hover ${Tooltip} {
    visibility: visible;
    opacity: 1;
  }
`;

/**
 * Get tooltip text based on leaf count and tier
 */
function getTooltipText(count, tier) {
  const countText =
    count === 3
      ? "Great variety"
      : count === 2
        ? "Has entrees"
        : "Limited options";

  const tierText =
    tier === "green-solid"
      ? "Restaurant-verified"
      : tier === "green-outline"
        ? "Confirmed vegan options"
        : "AI predicted";

  return `${countText} \u2022 ${tierText}`;
}

/**
 * LeafRating Component
 *
 * Displays 1-3 leaf icons with color indicating verification tier:
 * - Gray: Ad hoc search with only ML predictions
 * - Green outline: Has guaranteed vegan items or is a saved restaurant
 * - Solid green: Verified restaurant (details: true)
 *
 * @param {Object} props
 * @param {1|2|3} props.count - Number of leaves to display
 * @param {'gray'|'green-outline'|'green-solid'} props.tier - Verification tier
 * @param {boolean} props.showTooltip - Whether to show tooltip on hover
 */
const LeafRating = ({ count = 1, tier = "gray", showTooltip = true }) => {
  const [tooltipStyle, setTooltipStyle] = React.useState({});
  const wrapperRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (wrapperRef.current && showTooltip) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const tooltipHeight = 40; // Approximate tooltip height
      const tooltipWidth = 180; // Approximate tooltip width
      const spaceAbove = rect.top;
      const viewportWidth = window.innerWidth;
      const centerX = rect.left + rect.width / 2;

      // Calculate horizontal position - keep tooltip within viewport
      let left = centerX;
      let transformX = "-50%";

      // If tooltip would go off right edge
      if (centerX + tooltipWidth / 2 > viewportWidth - 10) {
        left = viewportWidth - 10;
        transformX = "-100%";
      }
      // If tooltip would go off left edge
      else if (centerX - tooltipWidth / 2 < 10) {
        left = 10;
        transformX = "0%";
      }

      // If not enough space above, show tooltip below
      if (spaceAbove < tooltipHeight + 16) {
        setTooltipStyle({
          top: rect.bottom + 8,
          left,
          transform: `translateX(${transformX})`,
        });
      } else {
        setTooltipStyle({
          top: rect.top - 8,
          left,
          transform: `translateX(${transformX}) translateY(-100%)`,
        });
      }
    }
  };

  const leaves = [];
  for (let i = 0; i < count; i++) {
    leaves.push(<LeafIcon key={i} className={`tier-${tier}`} />);
  }

  if (showTooltip) {
    return (
      <LeafContainer className="has-tooltip">
        <TooltipWrapper ref={wrapperRef} onMouseEnter={handleMouseEnter}>
          {leaves}
          <Tooltip style={tooltipStyle}>{getTooltipText(count, tier)}</Tooltip>
        </TooltipWrapper>
      </LeafContainer>
    );
  }

  return <LeafContainer>{leaves}</LeafContainer>;
};

LeafRating.propTypes = {
  count: PropTypes.oneOf([1, 2, 3]),
  tier: PropTypes.oneOf(["gray", "green-outline", "green-solid"]),
  showTooltip: PropTypes.bool,
};

export default LeafRating;
